/* eslint-disable */
/* disbale-ts */
import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { BASE_API_URL, BASE_IMAGE_URL } from '../env';
import { Modal, Image, Spin } from 'antd';
import {
  ArrowsAltOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import callApi from '../Api/config';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
type PresignedContent = { id: any; data: any } | null;

function PdfViewer(props: any) {
  const { photo, fileName, isSmallPreview }: any = props;
  const [open, setOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<PresignedContent>();
  const [error, setError] = useState<string | null>(null);
  const [isImage, setIsImage] = useState(false);
  const [pdfLoadError, setPdfLoadError] = useState(false);

  const handleClose = () => setOpen(false);

  const checkIfImage = (url: string) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];

    if (fileName && imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext))) {
      return true;
    }

    if (!url) return false;
    const cleanUrl = url.split('?')[0].split('#')[0];
    return imageExtensions.some(ext => cleanUrl.toLowerCase().endsWith(ext));
  };

  const fixImageUrl = (url: string, fileName?: string): string => {
    if (!url) return url;

    // Check if it's an image file - check URL path
    const urlPath = url.split('?')[0].split('#')[0];
    const hasImageExt =
      urlPath.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i) ||
      (fileName && fileName.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i));

    if (!hasImageExt) return url;

    // If URL has response-content-type=application/pdf, fix it to correct image type
    if (
      url.includes('response-content-type=application%2Fpdf') ||
      url.includes('response-content-type=application/pdf') ||
      url.includes('response-content-type=application%252Fpdf')
    ) {
      // Determine correct content-type from file extension
      let correctContentType = 'image/jpeg'; // Default to JPEG
      const ext = (
        urlPath.match(/\.(\w+)$/i)?.[1] ||
        (fileName && fileName.match(/\.(\w+)$/i)?.[1]) ||
        ''
      ).toLowerCase();

      if (ext === 'png') correctContentType = 'image/png';
      else if (ext === 'gif') correctContentType = 'image/gif';
      else if (ext === 'webp') correctContentType = 'image/webp';
      else if (ext === 'bmp') correctContentType = 'image/bmp';

      // Replace the incorrect content-type (handle both encoded and double-encoded)
      const encodedType = encodeURIComponent(correctContentType);
      url = url.replace(
        /response-content-type=application%252Fpdf/gi,
        `response-content-type=${encodedType}`
      );
      url = url.replace(
        /response-content-type=application%2Fpdf/gi,
        `response-content-type=${encodedType}`
      );
      url = url.replace(
        /response-content-type=application\/pdf/gi,
        `response-content-type=${encodedType}`
      );
    }

    return url;
  };

  const handlePresignedUrl = async (photo: any) => {
    try {
      setError(null);

      if (photo?.startsWith('http')) {
        setPdfUrl({ id: 'direct', data: fixImageUrl(photo, fileName) });
        setIsImage(checkIfImage(photo));
        return;
      }

      if (photo?.startsWith(BASE_IMAGE_URL)) {
        setPdfUrl({ id: 'direct', data: fixImageUrl(photo, fileName) });
        setIsImage(checkIfImage(photo));
        return;
      }

      const encodedPhoto = encodeURIComponent(photo);
      const result = await callApi(`/s3/presigned-url/${encodedPhoto}`, 'GET');

      if (result?.data?.data) {
        setPdfUrl({ id: photo, data: fixImageUrl(result.data.data, fileName) });
        setIsImage(checkIfImage(photo));
      } else {
        const fallbackUrl = `${BASE_IMAGE_URL}/${photo}`;
        setPdfUrl({ id: photo, data: fixImageUrl(fallbackUrl, fileName) });
        setIsImage(checkIfImage(photo));
      }
    } catch (error) {
      console.error('Error fetching presigned URL:', error);
      setError('Failed to load file. Please try again later.');

      if (photo) {
        const fallbackUrl = `${BASE_IMAGE_URL}/${photo}`;
        setPdfUrl({ id: photo, data: fixImageUrl(fallbackUrl, fileName) });
        setIsImage(checkIfImage(photo));
      }
    }
  };

  useEffect(() => {
    if (photo) {
      handlePresignedUrl(photo);
    }
  }, [photo]);

  if (error) {
    return <div style={{ color: 'red' }}>{error}</div>;
  }

  const containerStyle = isSmallPreview
    ? {
        position: 'relative' as const,
        display: 'flex',
        width: '100%',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column' as const,
        backgroundColor: '#ffffff',
        border: '1px solid #d9d9d9',
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        padding: '0',
        margin: '0',
        ':hover': {
          borderColor: '#40a9ff',
          boxShadow: '0 0 0 2px rgba(24,144,255,0.2)',
        },
      }
    : {
        position: 'relative' as const,
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column' as const,
        padding: '0',
        margin: '0',
      };

  const smallPreviewStyle = {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fafafa',
    padding: '0',
    borderRadius: '8px',
    position: 'relative' as const,
  };

  return (
    <>
      <div
        className="preview-container"
        style={containerStyle}
        onClick={() => !isImage && (!isSmallPreview || setOpen(true))}
      >
        {/* Loading skeleton while URL is being fetched */}
        {!pdfUrl?.data && !error && isSmallPreview && (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              gap: '6px',
            }}
          >
            <Spin size="small" />
            <div style={{ fontSize: '10px', color: '#bfbfbf' }}>Loading...</div>
          </div>
        )}
        {pdfUrl?.data && isImage ? (
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Image
              key={pdfUrl.id}
              src={pdfUrl.data}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: isSmallPreview ? '100%' : '300px',
                objectFit: 'contain',
                borderRadius: '4px',
                padding: '0',
              }}
              preview={{
                mask: (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <ZoomInOutlined style={{ fontSize: '24px' }} />
                    <span>Preview</span>
                  </div>
                ),
              }}
            />
          </div>
        ) : (
          pdfUrl?.data && (
            <div style={isSmallPreview ? smallPreviewStyle : { width: '100%' }}>
              {isSmallPreview ? (
                /* PDF small preview card — reliable styled indicator, click opens full modal */
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background: 'linear-gradient(135deg, #fff1f0 0%, #fff7f0 100%)',
                    borderRadius: '8px',
                    border: '1px solid #ffd8bf',
                    cursor: 'pointer',
                    padding: '6px',
                    boxSizing: 'border-box',
                    transition: 'box-shadow 0.2s ease',
                  }}
                  onClick={() => setOpen(true)}
                >
                  <FilePdfOutlined style={{ fontSize: '38px', color: '#fa541c' }} />
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#fa541c',
                      marginTop: '6px',
                      fontWeight: 700,
                      letterSpacing: '0.5px',
                    }}
                  >
                    PDF
                  </div>
                  <div style={{ fontSize: '9px', color: '#8c8c8c', marginTop: '2px' }}>
                    Click to preview
                  </div>
                </div>
              ) : (
                <embed
                  key={pdfUrl.id}
                  src={pdfUrl.data}
                  type="application/pdf"
                  style={{
                    width: '100%',
                    height: '230px',
                    border: '1px solid #e8e8e8',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                  }}
                />
              )}
            </div>
          )
        )}
        {!isImage && (
          <button
            style={{
              position: 'absolute',
              right: '15px',
              bottom: '40px',
              outline: 'none',
              border: 'none',
              fontSize: '16px',
              borderRadius: '12px',
              width: '24px',
              height: '24px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
            onClick={(e: any) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen(true);
            }}
          >
            <ArrowsAltOutlined style={{ fontSize: '12px' }} />
          </button>
        )}
      </div>

      {!isImage && (
        <Modal
          className="pdf-modal"
          open={open}
          onCancel={handleClose}
          width="95%"
          style={{ top: 20 }}
          bodyStyle={{ padding: 0 }}
          footer={null}
        >
          <div style={{ width: '100%', height: '90vh', display: 'flex', justifyContent: 'center' }}>
            {pdfUrl?.data && (
              <embed
                key={pdfUrl.id}
                src={pdfUrl.data}
                type="application/pdf"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
              />
            )}
          </div>
        </Modal>
      )}
    </>
  );
}

export default PdfViewer;
